import Const from 'constants';
import {resolveExposuresOverlap} from 'helpers/exposure';
import {getTeamNamesOfGames} from 'helpers/game-helper';
import {generateSignature} from 'helpers/hash';

const defaultTeamState = {
    active: false,
    checked: true,
    exposure: {min: 0, max: 100},
    lineGroups: Const.DEFAULT_VALUES_OF_LINEUPS_NHL_FILTER.lineGroups,
    ppLines: Const.DEFAULT_VALUES_OF_LINEUPS_NHL_FILTER.ppLines,
    isDefensemanIncluded: false,
};

const defaultStackState = {
    exposure: {
        min: 0,
        max: 100,
    },
    checked: true,
    teams: {},
};

const defaultStacksState = {
    [Const.NHL_TEAM_STACKING_FIRST_STACK]: cp(defaultStackState),
    [Const.NHL_TEAM_STACKING_SECOND_STACK]: cp(defaultStackState),
    [Const.NHL_TEAM_STACKING_THIRD_STACK]: cp(defaultStackState),
};

const defaultCombinationsState = {
    '2-2-2': {
        comboId: 'a1',
        checked: false,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 2,
        },
        isDefensemanMandatory: false,
    },
    '3-2-X': {
        comboId: 'b1',
        checked: true,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 3,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 2,
        },
        isDefensemanMandatory: false,
    },
    '3-2-2': {
        comboId: 'c1',
        checked: false,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 3,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 2,
        },
        isDefensemanMandatory: false,
    },
    '3-3-X': {
        comboId: 'd1',
        checked: true,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 3,
        },
        isDefensemanMandatory: false,
    },
    '3-3-2': {
        comboId: 'e1',
        checked: false,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 3,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 2,
        },
        isDefensemanMandatory: false,
    },
    '4-2-X': {
        comboId: 'f1',
        checked: true,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 4,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 2,
        },
        isDefensemanMandatory: true,
    },
    '4-2-2': {
        comboId: 'a2',
        checked: false,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 4,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 2,
        },
        isDefensemanMandatory: true,
    },
    '4-3-X': {
        comboId: 'b2',
        checked: true,
        exposure: {
            min: 0,
            max: 100,
        },
        playersQuantity: {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 4,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 3,
        },
        isDefensemanMandatory: true,
    },
};

const combinationsState = cp(defaultCombinationsState);

const defaultState = {
    enabled: false,
    signature: null,
    data: {
        stacks: cp(defaultStacksState),
        combinations: cp(combinationsState),
        chooseTeamsRandomly: false,
        forceTeamsToStacking: true,
    },
    isEnabled() {
        return this.enabled;
    },
    getStateId() {
        return 'NhlTeamStacking.Settings';
    },
    getStacks() {
        return {...this.data.stacks};
    },
    getCombinations() {
        return {...this.data.combinations};
    },
    getCombination(comboId) {
        for (const combination of Object.values(this.data.combinations)) {
            if (combination.comboId === comboId) {
                return combination;
            }
        }

        return null;
    },
    getCheckedCombinations() {
        const combos = {};
        Object.keys(this.data.combinations).map((comboKey) => {
            const combo = this.data.combinations[comboKey];
            if (combo.checked) {
                combos[comboKey] = {...combo};
            }
        });
        return combos;
    },
    isThereCheckedCombination() {
        for (let combo of Object.values(this.data.combinations)) {
            if (combo.checked === true) {
                return true;
            }
        }
        return false;
    },
    isCombinationChecked(key) {
        return this.data.combinations[key].checked;
    },
    getCombinationExposure(key, exposureType) {
        return this.data.combinations[key].exposure[exposureType];
    },
    isTeamsChosenRandomly() {
        return Boolean(this.data.chooseTeamsRandomly);
    },
    isTeamsForcedToStacking() {
        return Boolean(this.data.forceTeamsToStacking);
    },
    isAllActiveStackTeamsCheckedByStackNumber(stackNumber) {
        const teams = this.getActiveTeamsByStackNumber(stackNumber);
        for (let team of Object.values(teams)) {
            if (!team.checked) {
                return false;
            }
        }

        return true;
    },
    getStackTeamsExposureByTypeAndStackNumber(exposureType, stackNumber) {
        return this.data.stacks[stackNumber].exposure[exposureType];
    },
    getStackTeamsMinExposureByStackNumber(stackNumber) {
        return this.getStackTeamsExposureByTypeAndStackNumber('min', stackNumber);
    },
    getStackTeamsMaxExposureByStackNumber(stackNumber) {
        return this.getStackTeamsExposureByTypeAndStackNumber('max', stackNumber);
    },
    getLineGroupsByStackNumber(stackNumber) {
        const lineGroups = {...Const.DEFAULT_VALUES_OF_LINEUPS_NHL_FILTER.lineGroups};

        Object.keys(lineGroups).map((lineGroupKey) => {
            lineGroups[lineGroupKey] = true;
        });

        const teams = this.data.stacks[stackNumber].teams;

        Object.values(teams).map((team) => {
            if (!team.active) {
                return;
            }

            const teamLineGroups = team.lineGroups;
            Object.keys(teamLineGroups).map((lineGroupKey) => {
                if (teamLineGroups[lineGroupKey] === false) {
                    lineGroups[lineGroupKey] = false;
                }
            });
        });

        return lineGroups;
    },
    getTeamLineGroups(stackNumber, team) {
        return this.data.stacks[stackNumber].teams[team].lineGroups ?? {};
    },
    getPPLinesByStackNumber(stackNumber) {
        const ppLines = {...Const.DEFAULT_VALUES_OF_LINEUPS_NHL_FILTER.ppLines};

        Object.keys(ppLines).map((lineGroupKey) => {
            ppLines[lineGroupKey] = true;
        });

        const teams = this.data.stacks[stackNumber].teams;

        Object.values(teams).map((team) => {
            if (!team.active) {
                return;
            }

            const teamPPLines = team.ppLines;
            Object.keys(teamPPLines).map((ppLineKey) => {
                if (teamPPLines[ppLineKey] === false) {
                    ppLines[ppLineKey] = false;
                }
            });
        });

        return ppLines;
    },
    getTeamPPLines(stackNumber, team) {
        return this.data.stacks[stackNumber].teams[team].ppLines ?? {};
    },
    isTeamCheckedInStackNumber(team, stackNumber) {
        const stack = this.data.stacks[stackNumber];
        if (stack.teams?.[team].hasOwnProperty('checked')) {
            return stack.teams[team].checked;
        }

        return stack.checked;
    },
    isTeamDefensemanIncluded(team, stackNumber) {
        stackNumber ??= 1;
        const stack = this.data.stacks[stackNumber];
        if (stack.teams?.[team].hasOwnProperty('isDefensemanIncluded')) {
            return stack.teams[team].isDefensemanIncluded;
        }

        return false;
    },
    getTeamExposureInStackNumber(team, exposureType, stackNumber) {
        const stack = this.data.stacks[stackNumber];
        if (stack.teams?.[team].hasOwnProperty('exposure')) {
            return stack.teams[team].exposure[exposureType];
        }

        return stack.exposure[exposureType];
    },
    getTotalMinExposureOfCheckedActiveTeamsInStackNumber(stackNumber) {
        const stack = this.data.stacks[stackNumber];

        let totalMinExposure = 0;
        Object.values(stack.teams).map((teamItem) => {
            if (teamItem.checked && teamItem.active) {
                totalMinExposure += teamItem.exposure.min;
            }
        });

        return totalMinExposure;
    },
    getTotalMinExposureOfCheckedStackCombinations() {
        return this.getTotalExposureOfCheckedStackCombinations('min');
    },
    getTotalMaxExposureOfCheckedStackCombinations() {
        return this.getTotalExposureOfCheckedStackCombinations('max');
    },
    getTotalExposureOfCheckedStackCombinations(exposureType) {
        const combinations = this.data.combinations;

        let totalExposure = 0;
        Object.values(combinations).map((stackItem) => {
            if (stackItem.checked) {
                totalExposure += stackItem.exposure[exposureType];
            }
        });

        return totalExposure;
    },
    getActiveTeamsByStackNumber(stackNumber) {
        const activeTeams = {};

        if (!this.data.stacks.hasOwnProperty(stackNumber)) {
            return activeTeams;
        }

        const {teams} = this.data.stacks[stackNumber];

        Object.keys(teams).map((teamName) => {
            if (teams[teamName].active) {
                activeTeams[teamName] = teams[teamName];
            }
        });

        return activeTeams;
    },
    getActiveAndCheckedTeamsByStackNumber(stackNumber) {
        const activeTeams = {};

        if (!this.data.stacks.hasOwnProperty(stackNumber)) {
            return activeTeams;
        }

        const {teams} = this.data.stacks[stackNumber];

        Object.keys(teams).map((teamName) => {
            if (teams[teamName].active && teams[teamName].checked) {
                activeTeams[teamName] = teams[teamName];
            }
        });

        return activeTeams;
    },
    getExposureSumOfCheckedTeamsByStackNumber(stackNumber) {
        const teams = this.getActiveAndCheckedTeamsByStackNumber(stackNumber);
        let exposureSum = 0;
        Object.keys(teams).map((teamName) => {
            exposureSum += this.getTeamMaxExposureInStackNumber(teamName, stackNumber);
        });

        return exposureSum;
    },
    getCheckedTeamNamesByStackNumber(stackNumber) {
        const teams = this.getActiveAndCheckedTeamsByStackNumber(stackNumber);

        return Object.keys(teams).map((teamName) => teamName);
    },
    getTeamMinExposureInStackNumber(team, stackNumber) {
        return this.getTeamExposureInStackNumber(team, 'min', stackNumber);
    },
    getTeamMaxExposureInStackNumber(team, stackNumber) {
        return this.getTeamExposureInStackNumber(team, 'max', stackNumber);
    },
    getCombinationMinExposureValue(comboKey) {
        return this.getCombinationExposure(comboKey, 'min');
    },
    getCombinationMaxExposureValue(comboKey) {
        return this.getCombinationExposure(comboKey, 'max');
    },
    isDefensemanMandatoryInCombinationStack(combo, stackNumber) {
        const playersQuantityWithMandatoryDefenseman = 4;

        return combo.playersQuantity[stackNumber] >= playersQuantityWithMandatoryDefenseman;
    },
    getSignature() {
        return this.signature;
    },
};

export default function (state = defaultState, action) {
    state = {...state};

    if (action.type === Const.NHL_TEAM_STACKING_IS_USED) {
        state.enabled = Boolean(action.use);
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_COMBO_IS_CHECKED) {
        const combination = state.data.combinations[action.comboKey];
        combination.checked = action.checked;
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_COMBO_EXPOSURE) {
        const combination = state.data.combinations[action.comboKey];
        combination.exposure = action.exposure;
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_TEAMS_ARE_CHOSEN_RANDOMLY) {
        state.data.chooseTeamsRandomly = Boolean(action.chooseTeamsRandomly);
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_TEAMS_ARE_FORCED_TO_STACKING) {
        state.data.forceTeamsToStacking = Boolean(action.forceTeamsToStacking);
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_TEAMS) {
        Object.values(state.data.stacks).map((stack) => {
            const teams = {};

            Object.keys(stack.teams).map((teamName) => {
                teams[teamName] = stack.teams[teamName];
                teams[teamName].active = inArray(teamName, action.teams);
            });

            stack.teams = teams;
        });
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_STACK_TEAMS_IS_CHECKED) {
        const stack = state.data.stacks[action.stackNumber];
        stack.checked = action.checked;
        Object.values(stack.teams).map((team) => {
            if (team.active) {
                team.checked = action.checked;
            }
        });
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_STACK_TEAMS_EXPOSURE) {
        const stack = state.data.stacks[action.stackNumber];
        Object.keys(action.exposure).map((exposureType) => {
            const value = action.exposure[exposureType];
            stack.exposure[exposureType] = value;

            Object.values(stack.teams).map((team) => {
                const currentExposure = {...team.exposure};
                currentExposure[exposureType] = value;
                team.exposure = resolveExposuresOverlap(currentExposure, exposureType);
            });
        });
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_TEAM_LINE_GROUP_IS_CHECKED) {
        const teams = state.data.stacks[action.stackNumber].teams;
        Object.values(teams).map((team) => {
            if (team.active) {
                team.lineGroups = {...team.lineGroups, ...action.lineGroups};
            }
        });
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_TEAM_LINE_GROUPS) {
        const team = state.data.stacks[action.stackNumber].teams[action.team];
        team.lineGroups = {...team.lineGroups, ...action.lineGroups};
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_TEAM_PP_LINE_IS_CHECKED) {
        const teams = state.data.stacks[action.stackNumber].teams;
        Object.values(teams).map((team) => {
            if (team.active) {
                team.ppLines = {...team.ppLines, ...action.ppLines};
            }
        });
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_TEAM_PP_LINES) {
        const team = state.data.stacks[action.stackNumber].teams[action.team];
        team.ppLines = {...team.ppLines, ...action.ppLines};
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_TEAM_IS_CHECKED) {
        const stack = state.data.stacks[action.stackNumber];
        if (!stack.teams.hasOwnProperty(action.team)) {
            stack.teams[action.team] = {...defaultTeamState};
        }
        stack.teams[action.team].checked = Boolean(action.checked);
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_TEAM_DEFENSEMAN_IS_INCLUDED) {
        const stack = state.data.stacks[action.stackNumber];
        if (!stack.teams.hasOwnProperty(action.team)) {
            stack.teams[action.team] = {...defaultTeamState};
        }
        stack.teams[action.team].isDefensemanIncluded = Boolean(action.checked);
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_TEAM_EXPOSURE) {
        const stack = state.data.stacks[action.stackNumber];
        if (!stack.teams.hasOwnProperty(action.team)) {
            stack.teams[action.team] = {...defaultTeamState};
        }
        stack.teams[action.team].exposure = action.exposure;
    } else if (action.type === Const.NHL_TEAM_STACKING_MIRROR_STACK) {
        state.data.stacks[action.destinationStackNumber] = cp(state.data.stacks[action.sourceStackNumber]);
    } else if (action.type === Const.NHL_TEAM_STACKING_SET_DATA_FROM_STORAGE) {
        state.data = cp(action.data);
    } else if (action.type === Const.GET_GAMES_SUCCESS) {
        const teamNames = getTeamNamesOfGames(action.data);

        Object.values(state.data.stacks).map((stack) => {
            const teams = {};

            teamNames.map((teamName) => {
                teams[teamName] = teamName in stack.teams ? stack.teams[teamName] : cp(defaultTeamState);
            });

            stack.teams = teams;
        });
    } else if (action.type === Const.USE_HITTERS_STACK) {
        if (action.data) {
            state.enabled = false;
        }
    } else if (action.type === Const.FILTER_BY_SITE) {
        state = {
            ...defaultState,
            data: {
                stacks: cp(defaultStacksState),
                combinations: cp(defaultCombinationsState),
                chooseTeamsRandomly: false,
                forceTeamsToStacking: true,
            },
            enabled: false,
            signature: generateSignature(),
        };
    } else if (action.type === Const.RESET_ALL_SETTINGS) {
        const {stacks} = state.data;
        const stacksState = cp(defaultStacksState);

        Object.keys(stacks).map((stackGroup) => {
            Object.keys(stacks[stackGroup].teams).map((teamName) => {
                stacksState[stackGroup].teams[teamName] = {...defaultTeamState, active: true};
            });
        });

        state = {
            ...defaultState,
            data: {
                stacks: stacksState,
                combinations: cp(combinationsState),
                chooseTeamsRandomly: false,
                forceTeamsToStacking: true,
            },
            enabled: false,
            signature: generateSignature(),
        };
    } else if (action.type === Const.FILTER_BY_SLATE) {
        state = {
            ...defaultState,
            data: {
                stacks: cp(defaultStacksState),
                combinations: cp(combinationsState),
                chooseTeamsRandomly: false,
                forceTeamsToStacking: true,
            },
            enabled: false,
            signature: generateSignature(),
        };
    } else if (action.type === Const.NHL_LEGACY_TEAM_STACKING_IS_USED) {
        if (Boolean(action.use)) {
            state.enabled = false;
        }
    }

    return state;
}
